import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const errorMessage = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.reason === "incomplete") {
    return `Please complete: ${(d.missing || []).join(", ")}.`;
  }
  if (typeof d === "string") return d;
  return fallback;
};

// Field keys the server reported as missing or wrong, for red marking.
export const errorFields = (e) => {
  const d = e?.response?.data?.detail;
  return d && typeof d === "object" && d.reason === "incomplete" ? (d.fields || []) : [];
};

export function useCommission(token, asQuery, enabled) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const base = `${API}/api/public/proposals/${encodeURIComponent(token)}/commission`;
  const url = useCallback((path = "") => `${base}${path}${asQuery}`, [base, asQuery]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(url(""), { withCredentials: true });
      setState(data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, "Commissioning is unavailable right now."));
    }
  }, [url]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  useEffect(() => {
    if (!state?.busy) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [state, load]);

  const post = useCallback(async (path, body) => {
    const { data } = await axios.post(url(path), body ?? {}, { withCredentials: true, timeout: 60000 });
    setState(data);
    return data;
  }, [url]);

  const saveAnswers = useCallback(async (answers) => {
    const { data } = await axios.put(url("/answers"), { answers }, { withCredentials: true });
    setState((s) => ({ ...(s || {}), answers: data.answers, deadline: data.deadline, timeline: data.timeline }));
    return data;
  }, [url]);

  const uploadDraft = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await axios.post(url("/drafts"), fd, { withCredentials: true, timeout: 120000 });
    await load();
  }, [url, load]);

  const removeDraft = useCallback(async (id) => {
    await axios.delete(url(`/drafts/${id}`), { withCredentials: true });
    await load();
  }, [url, load]);

  return {
    state,
    error,
    reload: load,
    start: () => post("/start"),
    chooseRoute: (route) => post("/route", { route }),
    saveAnswers,
    sign: (body) => post("/sign", body),
    requestDeadlineCheck: () => post("/deadline-check"),
    requestChange: (text) => post("/change-request", { text }),
    submitClientPaper: (answers) => post("/client-paper", { answers }),
    uploadDraft,
    removeDraft,
    previewUrl: url("/preview.pdf"),
    executedUrl: url("/executed.pdf"),
    versionUrl: (index) => url(`/versions/${index}.pdf`),
  };
}
